import React from 'react';

import { translate } from '~l10n/client';

import { ActionMenuItem } from '~shared/components/ActionMenu/ActionMenuItem';
import { Divider } from '~shared/components/Divider';
import { Direction, Mode, Popper } from '~shared/components/Popper/Popper';

// type test
export interface Action<T = object> {
  /* if this has a custom class it can be applied to the aciton */
  className?: string;
  /* The actual action tht is being called. always remember to call close */
  action: ({ data, close }: { data: T[]; close: () => void }) => void;
  /* React uses this to optimize rendering */
  key: string;
  /* the text that will display in a dropdown menu */
  title: string;
  /* more detailed text to explain what the action does */
  description?: string;
  /* can apply a custom colour to the icon or text here */
  color?: string;
  /* If this function returns false, then the item will be hidden from all menus*/
  constraint?: ({ data }: { data: T[] }) => boolean;
  /* used to only show actions in the bulk actions are */
  isBulkAction?: boolean;
  /* if this returns true, then the action is considered disabled and should show greyed out */
  disabled?: ({ data }: { data: T[] }) => boolean;
  /* this is required to show in the bulk action menu */
  icon?: React.ReactNode;
  /* show a divider before this action */
  divider?: boolean;
  /* what can be typed to access this action? */
  searchKey?: string;
  /* if this is true, then the action only appears in devlopment mode */
  isDevOnly?: true;
  /* for testing */
  testId?: string;
  /* A callback to handle a potential paywall display */
  paywallCallback?: () => void;
  /* if this is true, then it can display a pro badge */
  showProBadge?: boolean;
}

interface Props<T> {
  data?: T[] | null;
  actions: Action<T>[];
  children: React.ReactNode;
  direction?: Direction;
  mode?: Mode;
  isDisabled?: boolean;
  enableCount?: boolean;
  width?: string;
}

const isDevOnlyAction = (action) =>
  __DEV__ ? true : action?.isDevOnly !== true;

function createCheckForDataAndBulkActions<T>(data: T[]) {
  return ({ constraint, isBulkAction }: Action<T>) => {
    // if there is no data to act on, show all options
    if (!data) return true;
    // Don't show action if it's a bulk action
    if (isBulkAction) return false;
    // run constraint function (removes item from visibility)
    if (typeof constraint === 'function') {
      return constraint({ data });
    } else {
      return true;
    }
  };
}

/**
 *  Simple Component that renders an action menu from an array of action objects
 */
export const ActionMenu = <T extends object>({
  children,
  actions,
  data = null,
  direction = 'bottom-end',
  isDisabled,
  mode = 'click',
  enableCount,
  width = '',
}: Props<T>) => {
  const checkForDataAndBulkAction = React.useMemo(
    () => createCheckForDataAndBulkActions(data),
    [data]
  );

  const filteredActions = React.useMemo(
    () =>
      (actions ?? []).filter(isDevOnlyAction).filter(checkForDataAndBulkAction),
    [actions, checkForDataAndBulkAction]
  );

  if (filteredActions.length === 0) return <>{children}</>;

  return (
    <Popper
      theme={'dark'}
      isDisabled={isDisabled}
      keyboardNavigable
      direction={direction}
      mode={mode}
      content={(closePopper) => (
        <div
          style={{ width: width }}
          className="w-70 shadow-modal rounded-1 bg-white py-1 h-full max-h-full overflow-y-auto"
        >
          {enableCount && data.length > 1 ? (
            <div tabIndex={-1}>
              <div className={'w-full p-3 text-xs text-grey-600 rounded-3'}>
                {translate('{0} items selected').format(data.length)}
              </div>
              <Divider />
            </div>
          ) : null}
          {filteredActions.map(
            ({
              key,
              className,
              disabled,
              color,
              action,
              divider,
              title,
              icon,
              searchKey = title,
              description,
              paywallCallback,
              showProBadge,
            }) => (
              <ActionMenuItem
                action={action}
                closePopper={closePopper}
                data={data}
                key={key}
                title={title}
                description={description}
                icon={icon}
                className={className}
                searchKey={searchKey}
                disabled={disabled}
                color={color}
                divider={divider}
                paywallCallback={paywallCallback}
                showProBadge={showProBadge}
              />
            )
          )}
        </div>
      )}
    >
      {children}
    </Popper>
  );
};
